/**
 * TTSManager - Cache-first Text-to-Speech manager for Kids Learning Zone (Web).
 *
 * Resolution order:
 *   1. MediaCacheManager IndexedDB cache (Blob URL)
 *   2. localStorage inline cache for short clips (< 100 chars)
 *   3. Server API (quickTTS for short text, submitTTS + poll for long text)
 *
 * Results are cached for offline use. Never throws - returns boolean.
 *
 * Usage:
 *   import TTSManager from './shared/TTSManager';
 *   const spoke = await TTSManager.speak('Hello world', { voice: 'emma' });
 *   await TTSManager.preCache(['Line one', 'Line two'], { voice: 'emma' });
 *   TTSManager.stop();
 */

import { quickTTS, submitTTS, pollTTS, pollUntilDone } from '../kidsLearningApi';
import MediaCacheManager from './MediaCacheManager';
import AudioChannelManager from './AudioChannelManager';

// ── Cache Key Helpers ────────────────────────────────────────────────────────

const SHORT_TEXT_THRESHOLD = 100;
const INLINE_PREFIX = 'tts_inline_';

/** Build the params object used as MediaCacheManager key. */
const buildTTSParams = (text, voice, engine) => ({
  text,
  voice: voice || 'default',
  engine: engine || 'pocket_tts',
});

/** Build localStorage key for inline short-clip cache. */
const getInlineKey = (text, voice, engine) => {
  return INLINE_PREFIX + MediaCacheManager.generateCacheKey('tts', buildTTSParams(text, voice, engine));
};

// ── Internal State ───────────────────────────────────────────────────────────

let _speaking = false;
let _cancelled = false;

// ── Internal Helpers ─────────────────────────────────────────────────────────

/**
 * Convert a base64 audio string to a Blob URL and optionally store it in the
 * IndexedDB cache via MediaCacheManager.
 * @returns {Promise<string|null>} Blob URL or null
 */
const _base64ToBlobUrl = async (base64Data, mediaType, params, format) => {
  try {
    const blobUrl = await MediaCacheManager.storeBase64(
      mediaType,
      params,
      base64Data,
      format ? `.${format}` : '.mp3',
    );
    return blobUrl;
  } catch (_) {
    // If IDB store fails, create a transient Blob URL directly
    try {
      const binaryStr = atob(base64Data);
      const bytes = new Uint8Array(binaryStr.length);
      for (let i = 0; i < binaryStr.length; i++) {
        bytes[i] = binaryStr.charCodeAt(i);
      }
      const blob = new Blob([bytes], { type: 'audio/mpeg' });
      return URL.createObjectURL(blob);
    } catch (__) {
      return null;
    }
  }
};

/**
 * Play audio through AudioChannelManager and manage speaking state.
 * @returns {Promise<boolean>} true if playback completed, false otherwise.
 */
const _playAudio = async (blobUrlOrBuffer, { onStart, onEnd, onError } = {}) => {
  try {
    _speaking = true;

    const success = await AudioChannelManager.playTTS(blobUrlOrBuffer, {
      onStart: () => {
        if (onStart) onStart();
      },
      onEnd: () => {
        _speaking = false;
        if (onEnd) onEnd();
      },
    });

    if (!success) _speaking = false;
    return success;
  } catch (err) {
    _speaking = false;
    if (onError) onError(err);
    return false;
  }
};

// ── TTSManager ───────────────────────────────────────────────────────────────

const TTSManager = {
  /**
   * Speak text using TTS with cache-first resolution.
   * Never throws - returns true if audio was played, false otherwise.
   *
   * @param {string} text - The text to speak.
   * @param {Object} [options]
   * @param {string} [options.voice] - Voice identifier.
   * @param {string} [options.engine] - TTS engine (default: 'pocket_tts').
   * @param {Function} [options.onStart] - Called when playback starts.
   * @param {Function} [options.onEnd] - Called when playback ends.
   * @param {Function} [options.onError] - Called on error (informational only).
   * @returns {Promise<boolean>} true if audio was spoken, false otherwise.
   */
  speak: async (text, { voice, engine, onStart, onEnd, onError } = {}) => {
    if (!text || typeof text !== 'string' || text.trim().length === 0) {
      return false;
    }

    _cancelled = false;

    const params = buildTTSParams(text, voice, engine);
    const inlineKey = getInlineKey(text, voice, engine);
    const isShort = text.length < SHORT_TEXT_THRESHOLD;

    try {
      // ── Step 1: Check MediaCacheManager IndexedDB cache ──
      let blobUrl = null;
      try {
        blobUrl = await MediaCacheManager.getAsync('tts', params);
      } catch (_) {
        // Cache miss or unavailable - continue
      }

      if (blobUrl && !_cancelled) {
        return await _playAudio(blobUrl, { onStart, onEnd, onError });
      }

      // ── Step 2: Check localStorage inline cache (short clips only) ──
      if (isShort && !_cancelled) {
        try {
          const cachedBase64 = localStorage.getItem(inlineKey);
          if (cachedBase64) {
            // Store to IndexedDB for faster future access and get Blob URL
            const diskUrl = await _base64ToBlobUrl(cachedBase64, 'tts', params);
            if (diskUrl && !_cancelled) {
              return await _playAudio(diskUrl, { onStart, onEnd, onError });
            }
          }
        } catch (_) {
          // localStorage unavailable - continue to server
        }
      }

      if (_cancelled) return false;

      // ── Step 3: Server API fallback ──
      let audioData = null;

      if (isShort) {
        // Quick TTS for short text - returns inline base64 or audio URL
        const response = await quickTTS(text, { voice, engine });
        audioData = response?.data || response;
      } else {
        // Submit + poll for longer text
        const submitResult = await submitTTS(text, { voice, engine });
        const taskId = submitResult?.data?.taskId || submitResult?.taskId;

        if (!taskId) {
          if (onError) onError(new Error('No taskId returned from TTS submit'));
          return false;
        }

        audioData = await pollUntilDone(
          () => pollTTS(taskId),
          { intervalMs: 2000, maxAttempts: 30 },
        );
        // Extract nested data if present
        if (audioData?.data) {
          audioData = audioData.data;
        }
      }

      if (_cancelled || !audioData) return false;

      // ── Cache the result for offline use ──
      let playableUrl = audioData.audioUrl || audioData.url || audioData.uri || null;

      if (audioData.base64) {
        // Cache inline base64 to localStorage for short clips
        if (isShort) {
          try {
            localStorage.setItem(inlineKey, audioData.base64);
          } catch (_) {
            // Non-critical - continue without inline caching
          }
        }

        // Store to IndexedDB via MediaCacheManager
        const diskUrl = await _base64ToBlobUrl(
          audioData.base64,
          'tts',
          params,
          audioData.format,
        );
        if (diskUrl) playableUrl = diskUrl;
      } else if (playableUrl) {
        // Cache remote URL to IndexedDB
        try {
          const downloadedUrl = await MediaCacheManager.download('tts', params, playableUrl);
          if (downloadedUrl) playableUrl = downloadedUrl;
        } catch (_) {
          // Cache failed - use remote URL directly
        }
      }

      if (!playableUrl || _cancelled) {
        if (onError) onError(new Error('No playable audio URI available'));
        return false;
      }

      return await _playAudio(playableUrl, { onStart, onEnd, onError });
    } catch (err) {
      if (onError) onError(err);
      return false;
    }
  },

  /**
   * Pre-cache TTS audio for multiple texts in parallel.
   * Useful for pre-loading narration for upcoming game screens.
   *
   * @param {string[]} texts - Array of texts to pre-cache.
   * @param {Object} [options]
   * @param {string} [options.voice] - Voice identifier.
   * @param {string} [options.engine] - TTS engine.
   * @returns {Promise<void>}
   */
  preCache: async (texts, { voice, engine } = {}) => {
    if (!Array.isArray(texts) || texts.length === 0) return;

    const tasks = texts.map(async (text) => {
      if (!text || typeof text !== 'string' || text.trim().length === 0) return;

      const params = buildTTSParams(text, voice, engine);
      const inlineKey = getInlineKey(text, voice, engine);

      // Skip if already cached in IndexedDB
      try {
        if (MediaCacheManager.has('tts', params)) return;
      } catch (_) {
        // Continue to fetch
      }

      const isShort = text.length < SHORT_TEXT_THRESHOLD;

      // Also skip if already in localStorage (short clips)
      if (isShort) {
        try {
          const cached = localStorage.getItem(inlineKey);
          if (cached) return;
        } catch (_) {
          // Continue to fetch
        }
      }

      try {
        let audioData = null;

        if (isShort) {
          const response = await quickTTS(text, { voice, engine });
          audioData = response?.data || response;
        } else {
          const submitResult = await submitTTS(text, { voice, engine });
          const taskId = submitResult?.data?.taskId || submitResult?.taskId;
          if (!taskId) return;

          const pollResult = await pollUntilDone(
            () => pollTTS(taskId),
            { intervalMs: 2000, maxAttempts: 30 },
          );
          audioData = pollResult?.data || pollResult;
        }

        if (!audioData) return;

        // Cache base64 data
        if (audioData.base64) {
          if (isShort) {
            try {
              localStorage.setItem(inlineKey, audioData.base64);
            } catch (_) {}
          }
          try {
            await MediaCacheManager.storeBase64(
              'tts',
              params,
              audioData.base64,
              audioData.format ? `.${audioData.format}` : '.mp3',
            );
          } catch (_) {}
        } else if (audioData.audioUrl || audioData.url || audioData.uri) {
          try {
            await MediaCacheManager.download(
              'tts',
              params,
              audioData.audioUrl || audioData.url || audioData.uri,
            );
          } catch (_) {}
        }
      } catch (_) {
        // Silently skip failed pre-cache items
      }
    });

    await Promise.allSettled(tasks);
  },

  /**
   * Stop current TTS playback.
   */
  stop: () => {
    _cancelled = true;
    _speaking = false;
    try {
      AudioChannelManager.stopTTS();
    } catch (_) {
      // AudioChannelManager may not be initialized
    }
  },

  /**
   * Check if TTS is currently speaking.
   * @returns {boolean}
   */
  isSpeaking: () => {
    return _speaking;
  },
};

export default TTSManager;
